import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import CreateUserModal from "../../component/places/modal/CreateUserModal";
import UsertableComponent from "../../component/usertable/UsertableComponent";
import services from "../../services/services";
import { AppDispatch, useAppDispatch } from "../../store/app/store";
import { getallUsersThank } from "../../store/features/getUsersSlice";
import { UserSearch } from "./UserSearch";
import Pagination from "@mui/material/Pagination";
import ExportUsersModal from "../../component/places/modal/ExportUserCsv";

const Users = () => {
  const { adminPermission } = useSelector((state: any) => state.counter);
  const dispatch: AppDispatch = useAppDispatch();
  const data = useSelector((state: any) => state.getUsers);
  const [searchParams, setSearchParams] = useSearchParams();

  // Restore page from URL on mount (e.g. /users?page=2)
  const initialPage = parseInt(searchParams.get("page") || "1", 10);

  const [displayUsers, setDisplayUsers] = useState<any[]>(data.users);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(
    data.pagination?.totalPages || 1
  );
  const itemsPerPage = 15;
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const debouncedFetchData = useMemo(
    () =>
      debounce((term: string) => {
        setCurrentPage(1);
        setSearchParams(term ? { page: "1", q: term } : { page: "1" }, { replace: true });
        fetchData(0, term);
      }, 500),
    []
  );
  useEffect(() => {
    setDisplayUsers(data.users || []);
    setTotalPages(data.pagination?.totalPages || 1);
  }, [data.users, data.pagination]);

  useEffect(() => {
    fetchData(initialPage - 1, searchQuery);
  }, []);

  const fetchData = useCallback(async (pageNo: number, query: string) => {
    try {
      setIsLocalLoading(true);
      const response = await services.getUser({
        page: pageNo + 1, // Fixed: pageNo is 0-based, API expects 1-based
        pageSize: itemsPerPage,
        search: query,
      });
      setDisplayUsers(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLocalLoading(false);
    }
  }, []);

  const handlePageClick = ({ selected }: { selected: number }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
    setSearchParams(searchQuery ? { page: String(newPage), q: searchQuery } : { page: String(newPage) }, { replace: true });
    fetchData(selected, searchQuery);
  };

  if (adminPermission?.is_admin == 2 && !adminPermission?.editUser) {
    return (
      <h1 className="text-3xl font-openSans font-bold text-aqua items-center flex justify-center h-[80vh]">
        You don't have permission
      </h1>
    );
  }
  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    searchTerm = searchTerm.trim();
    debouncedFetchData(searchTerm);
  };

  return (
    <div>
      <div className="flex px-12 w-full justify-between">
        <UserSearch
          searchQuery={searchQuery}
          onSearch={(term) => {
            handleSearch(term);
          }}
        />

        <div className="flex w-1/4 gap-4 justify-end mr-4">
          <CreateUserModal />
          <ExportUsersModal />  
        </div>
      </div>
      <div className=" h-[66vh] overflow-auto">
        {isLocalLoading ?
          <div className="flex w-full h-[63vh] justify-center mt-8">
            <h1 className="text-aqua text-3xl">Loading...</h1>
          </div> : <div className="mt-6">
            <UsertableComponent allUser={displayUsers} />
          </div>
        }
        {!displayUsers?.length && searchQuery && (
          <div className="w-full h-[20vh] flex justify-center items-center">
            <h2 className="text-center text-aqua font-bold font-openSans w-full text-4xl">
              <span className="text-red-600">{searchQuery}</span> is not available
            </h2>
          </div>
        )}
      </div>
      {displayUsers?.length > 0 && (
        <div className="flex justify-center mt-6 mb-6">
          <Pagination
            count={totalPages || 1}
            page={currentPage}
            onChange={(event, page) => handlePageClick({ selected: page - 1 })}
            siblingCount={3}
            variant="outlined"
            shape="rounded"
            color={"primary"}
            size="large"
            boundaryCount={2}
          />
        </div>
      )}
    </div>
  );
};

export default Users;
